
import * as Yup from 'yup';
import { useCallback, useMemo, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray } from 'react-hook-form';
// @mui
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
// hooks
// types
import { LoSam, LoSamApi } from 'src/types/losam';
import { LoSamCamera } from 'src/types/camera';
// components
import { CustomFile } from 'src/components/upload';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider from 'src/components/hook-form';
import { paths } from 'src/routes/paths';
import CustomLoadingButton from 'src/components/button/button-loadding-dark-model';
import { useLoSam } from '../hooks/use-losam';
import useVuonTrong from '../../vuontrong/hooks/use-vuontrong';

// Sub-components
import LoSamDetailForm from './lo-sam-detail-form';
import LoSamChiTietForm from './lo-sam-chi-tiet-form';
import LoSamCameraForm from './lo-sam-camera-form';
import LoSamSensorForm from './lo-sam-sensor-form';

// ----------------------------------------------------------------------

type Props = {
  currentLoSam?: LoSam;
  onClose: VoidFunction;
  onSave?: (data: Partial<LoSamApi>, file?: File) => Promise<void>;
};

interface FormValuesProps {
  vuonTrong_ID: number;
  tenLo: string;
  maLo: string;
  dienTich: number;
  loai: string;
  ghiChu: string;
  soHang: number;
  soCot: number;
  is_public: boolean;
  trangThai: number;
  coverUrl?: CustomFile | string | null;
  chiTietLoSam: {
    loSamLoaiTuoiId: number;
    tuoi: string;
    soLuong: number;
    trangThai: number;
  }[];
  cameras: LoSamCamera[];
  sensorIds: number[];
}

export default function LoSamNewEditForm({ currentLoSam, onClose, onSave }: Props) {
  const isEdit = !!currentLoSam;

  const { enqueueSnackbar } = useSnackbar();

  const {
    loaiTuoiOptions,
    loaiCameraOptions,
    addLoSam,
    editLoSam,
    uploadImage,
  } = useLoSam();
  const { gardens } = useVuonTrong();

  const NewLoSamSchema = Yup.object().shape({
    tenLo: Yup.string().required('Tên lô là bắt buộc'),
    maLo: Yup.string().required('Mã lô là bắt buộc'),
    dienTich: Yup.number()
      .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
      .required('Diện tích là bắt buộc')
      .min(0, 'Diện tích phải lớn hơn 0'),
    loai: Yup.string().required('Loại là bắt buộc'),
    ghiChu: Yup.string(),
    soHang: Yup.number()
      .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
      .required('Số hàng là bắt buộc')
      .min(1, 'Số hàng phải lớn hơn 0'),
    soCot: Yup.number()
      .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
      .required('Số cột là bắt buộc')
      .min(1, 'Số cột phải lớn hơn 0'),
    is_public: Yup.boolean(),
    coverUrl: Yup.mixed().nullable(),
    chiTietLoSam: Yup.array().of(
      Yup.object().shape({
        loSamLoaiTuoiId: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Loại tuổi là bắt buộc'),
        tuoi: Yup.string().required('Tuổi là bắt buộc'),
        soLuong: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Số lượng là bắt buộc')
          .min(1, 'Số lượng phải lớn hơn 0'),
        trangThai: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Trạng thái là bắt buộc'),
      })
    ),
    cameras: Yup.array().of(
      Yup.object().shape({
        loSamLoaiCameraId: Yup.number()
          .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : Number(o)))
          .required('Loại camera là bắt buộc'),
        rtsp: Yup.string().required('RTSP là bắt buộc'),
        userName: Yup.string(), 
        password: Yup.string(), 
      })
    ),
    sensorIds: Yup.array().of(Yup.number()),
  });

  const defaultValues = useMemo(
    () => ({
      vuonTrong_ID: currentLoSam?.vuonTrong_ID || 0,
      tenLo: currentLoSam?.tenLo || '',
      maLo: currentLoSam?.maLo || '',
      dienTich: currentLoSam?.dienTich || 0,
      loai: currentLoSam?.loai || '',
      trangThai: currentLoSam?.trangThai || 1,
      ghiChu: currentLoSam?.ghiChu || '',
      soHang: currentLoSam?.soHang || 0,
      soCot: currentLoSam?.soCot || 0,
      is_public: (currentLoSam?.trangThai ?? 1) === 1,
      coverUrl: currentLoSam?.hinhAnh || null,
      chiTietLoSam: [],
      cameras: [],
      sensorIds: [], 
    }),
    [currentLoSam]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewLoSamSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'chiTietLoSam',
  });

  const { fields: cameraFields, append: appendCamera, remove: removeCamera, replace: replaceCamera } = useFieldArray({
    control,
    name: 'cameras',
  });

  useEffect(() => {
    if (currentLoSam) {
      reset(defaultValues);

      if (currentLoSam.hinhAnh) {
        setValue('coverUrl', currentLoSam.hinhAnh);
      }

      const mappedChiTiet = (currentLoSam.loSamChiTiets || []).map((ct: any) => ({
        loSamLoaiTuoiId: ct.loSamLoaiTuoiId ?? ct.loSamLoaiTuoi?.id ?? 0,
        tuoi: ct.loSamLoaiTuoi?.tuoi || '',
        soLuong: ct.soLuong ?? 0,
        trangThai: ct.trangThai ?? 1,
      }));
      if (mappedChiTiet.length > 0) {
        replace(mappedChiTiet);
      }

      const mappedCameras = (currentLoSam.loSamCameras || []).map((cm: any) => ({
        id: cm.id || 0,
        loSamId: cm.loSamId || currentLoSam.loSam_ID || 0,
        loSamLoaiCameraId: cm.loSamLoaiCameraId ?? cm.loSamLoaiCamera?.id ?? 0,
        rtsp: cm.rtsp || '',
        trangThai: cm.trangThai ?? 1,
        userName: cm.userName || '',
        password: cm.password || '',
        action: cm.action ||'',
        onvifCamera: cm.onvifCamera || '',
        ipCamera: cm.ipCamera
      }));
      if (mappedCameras.length > 0) {
        replaceCamera(mappedCameras);
      }
       if (currentLoSam.sensorIds && currentLoSam.sensorIds.length > 0) {
      setValue('sensorIds', currentLoSam.sensorIds);
    }
    }
   
  }, [currentLoSam, defaultValues, reset, replace, replaceCamera, setValue]);

  const handleAddChiTiet = useCallback(() => {
    append({
      loSamLoaiTuoiId: 1,
      tuoi: '1 năm',
      soLuong: 1,
      trangThai: 1,
    });
  }, [append]);

  const handleRemoveChiTiet = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  const handleAddCamera = useCallback(() => {
    appendCamera({
      id: 0,
      loSamId: 0,
      loSamLoaiCameraId: 1,
      rtsp: '',
      trangThai: 1,
      password: '',
      userName: '',
      ipCamera: '',
      onvifCamera: '',
      action:''
    });
  }, [appendCamera]);

  const handleRemoveCamera = useCallback((index: number) => {
    removeCamera(index); 
  }, [removeCamera]);

  const handleLoaiTuoiChange = useCallback((index: number, value: string) => {
    const selectedTuoi = loaiTuoiOptions.find((option: any) => String(option.Value) === String(value));
    setValue(`chiTietLoSam.${index}.loSamLoaiTuoiId`, Number(value));
    setValue(`chiTietLoSam.${index}.tuoi`, (selectedTuoi as any)?.Text || '');
  }, [setValue, loaiTuoiOptions]);

  const onSubmit = useCallback(
    async (data: FormValuesProps) => {
      try {
        let imageUrl = '';

        if (data.coverUrl && typeof data.coverUrl !== 'string' && data.coverUrl instanceof File) {
          console.log('Uploading file:', data.coverUrl);
          imageUrl = await uploadImage(data.coverUrl);
        } else if (typeof data.coverUrl === 'string') {
          imageUrl = data.coverUrl;
        }

        const losamData: LoSamApi = {
          loSam_ID: isEdit && currentLoSam?.loSam_ID ? currentLoSam.loSam_ID : 0,
          vuonTrong_ID: Number(data.vuonTrong_ID) || 0,
          tenLo: data.tenLo,
          maLo: data.maLo,
          ngay: new Date(),
          dienTich: data.dienTich,
          loai: data.loai,
          ghiChu: data.ghiChu,
          soHang: data.soHang,
          soCot: data.soCot,
          hinhAnh: imageUrl,
          trangThai: data.trangThai || 1,
          loSamChiTiets: data.chiTietLoSam,
          loSamCameras: data.cameras,
          sensorIds : data.sensorIds,
        };

        if (isEdit && currentLoSam?.loSam_ID) {
          const response = await editLoSam({
            ...losamData,
          },
            data.coverUrl instanceof File ? data.coverUrl : undefined);
          if (response.messCode === 1 || response.messCode === 6) {
            enqueueSnackbar('Cập nhật lô sâm thành công!', { variant: 'success' });
            window.location.href = paths.dashboard.losam.list;
          } else {
            enqueueSnackbar(response.message || 'Có lỗi xảy ra khi cập nhật lô sâm!', { variant: 'error' });
          }
        } else if (onSave) {
          await onSave(losamData, data.coverUrl instanceof File ? data.coverUrl : undefined);
        } else {
          const response = await addLoSam(losamData, data.coverUrl instanceof File ? data.coverUrl : undefined);
          if (response.messCode === 1 || response.messCode === 6) {
            enqueueSnackbar('Tạo lô sâm thành công!', { variant: 'success' });
            window.location.href = paths.dashboard.losam.list;
          } else {
            enqueueSnackbar(response.message || 'Có lỗi xảy ra khi tạo lô sâm!', { variant: 'error' });
          }
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar(
          isEdit ? 'Có lỗi xảy ra khi cập nhật lô sâm!' : 'Có lỗi xảy ra khi tạo lô sâm!',
          { variant: 'error' }
        );
      }
    },
    [isEdit, addLoSam, editLoSam, uploadImage, enqueueSnackbar, onSave, currentLoSam]
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <LoSamDetailForm 
            gardens={gardens} 
            isEdit={isEdit} 
        />

        <LoSamChiTietForm 
            fields={fields} 
            loaiTuoiOptions={loaiTuoiOptions} 
            onAdd={handleAddChiTiet} 
            onRemove={handleRemoveChiTiet} 
            onLoaiTuoiChange={handleLoaiTuoiChange} 
        />

        <LoSamCameraForm 
            fields={cameraFields} 
            loaiCameraOptions={loaiCameraOptions} 
            onAdd={handleAddCamera} 
            onRemove={handleRemoveCamera} 
        />

        <LoSamSensorForm 
            currentLoSam={currentLoSam}
            sensorModels={currentLoSam?.sensorModels || []}
        />

        <Grid xs={12} md={12}>
            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                <CustomLoadingButton 
                    type="submit" 
                    variant="contained" 
                    loading={isSubmitting}
                >
                    {!isEdit ? 'Tạo mới' : 'Lưu thay đổi'}
                </CustomLoadingButton>
            </Stack>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
